import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { PageLayout } from '@/shared/components/PageLayout';
import { joinPluginNames } from '@/entities/system';
import { Drawer } from '@/shared/components/Drawer';
import { useStyles } from '@/pages/Systems/styles';
import { routes } from '@/pages/routes';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { SystemCreate } from './components/SystemCreate';
import { SystemDetails } from './components/SystemDetails';
import { useGetAllSystems } from './services/hooks/use-get-all-systems';
const rowsPerPageSetting = [10, 30, 50, 100, 1000];
const DEFAULT_PAGE_SIZE = 50;
const PAGE_NUMBER_PARAM = 'pageNumber';
const PAGE_SIZE_PARAM = 'pageSize';
// Пагинация хранится в query-параметрах, чтобы после F5 открывалась та же страница.
// Невалидные значения из адресной строки заменяются значениями по умолчанию.
const parsePagination = (search) => {
    const params = new URLSearchParams(search);
    const pageNumber = Number(params.get(PAGE_NUMBER_PARAM));
    const pageSize = Number(params.get(PAGE_SIZE_PARAM));
    return {
        pageNumber: Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1,
        pageSize: rowsPerPageSetting.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    };
};
export const Systems = () => {
    const classes = useStyles();
    const columns = [
        { value: 'id', label: 'ИД', minWidth: 40, render: (system) => system.id },
        { value: 'guid', label: 'Идентификатор', minWidth: 280, render: (system) => system.guid },
        { value: 'code', label: 'Код', minWidth: 160, render: (system) => system.code },
        { value: 'description', label: 'Описание', minWidth: 160, render: (system) => system.description },
        {
            value: 'serializationPluginsInfo',
            label: 'Сериализационный',
            minWidth: 180,
            render: (system) => joinPluginNames(system.serializationPluginsInfo),
        },
        {
            value: 'integrationPluginsInfo',
            label: 'Интеграционный',
            minWidth: 180,
            render: (system) => joinPluginNames(system.integrationPluginsInfo),
        },
        {
            value: 'asyncIntegrationPluginsInfo',
            label: 'Асинхронноинтеграционный',
            minWidth: 240,
            render: (system) => joinPluginNames(system.asyncIntegrationPluginsInfo),
        },
        { value: 'agentInfo', label: 'Агент', minWidth: 160, render: (system) => system.agentInfo?.name },
        {
            value: 'huHeadSecretInfos',
            label: 'Секреты',
            minWidth: 80,
            align: 'center',
            render: (system) => system.huHeadSecretInfos?.length ?? 0,
        },
    ];
    const history = useHistory();
    const { search } = useLocation();
    const { pageNumber, pageSize } = parsePagination(search);
    const { guid: openedSystemGuid } = useParams();
    const isAddingSystem = Boolean(useRouteMatch({ path: routes.systemAdd, exact: true }));
    const { data: systemsList } = useGetAllSystems(`?pageSize=${pageSize}&pageNumber=${pageNumber}`);
    // Drawer редактирования берёт систему из уже загруженного списка - без отдельного запроса.
    const openedSystem = systemsList?.huHeadSystemInfos?.find((system) => system.guid === openedSystemGuid);
    // Переходы между списком и drawer сохраняют query-параметры - иначе пагинация сбросится.
    const navigate = useCallback((pathname) => {
        history.push({ pathname, search: history.location.search });
    }, [history]);
    const setPagination = (nextPageNumber, nextPageSize, replace = false) => {
        const params = new URLSearchParams(history.location.search);
        params.set(PAGE_NUMBER_PARAM, String(nextPageNumber));
        params.set(PAGE_SIZE_PARAM, String(nextPageSize));
        const location = { search: `?${params.toString()}` };
        if (replace) {
            history.replace(location);
        }
        else {
            history.push(location);
        }
    };
    const totalCount = systemsList?.totalCount ?? 0;
    const totalPages = systemsList?.totalPages ?? 0;
    // После удаления строк или ручной правки URL страница может оказаться за пределами списка.
    useEffect(() => {
        if (totalPages > 0 && pageNumber > totalPages) {
            setPagination(totalPages, pageSize, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages, pageNumber, pageSize]);
    // Черновик номера страницы - применяется по Enter или потере фокуса, а не на каждый символ.
    const [pageDraft, setPageDraft] = useState(String(pageNumber));
    useEffect(() => {
        setPageDraft(String(pageNumber));
    }, [pageNumber]);
    const applyPageDraft = () => {
        const parsed = Number(pageDraft);
        if (!Number.isInteger(parsed)) {
            setPageDraft(String(pageNumber));
            return;
        }
        const nextPage = Math.min(Math.max(parsed, 1), Math.max(totalPages, 1));
        setPageDraft(String(nextPage));
        if (nextPage !== pageNumber) {
            setPagination(nextPage, pageSize);
        }
    };
    const handlePageKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            applyPageDraft();
        }
    };
    const handleCloseSystem = () => {
        navigate(routes.systems);
    };
    // useCallback - SystemCreate дёргает onCreated из useEffect по ответу бэкенда.
    const handleSystemCreated = useCallback((createdGuid) => {
        navigate(`/system/${createdGuid}`);
    }, [navigate]);
    const handleChangePage = (_, newPage) => {
        setPagination(newPage + 1, pageSize);
    };
    const handleChangeRowsPerPage = (event) => {
        setPagination(1, Number(event.target.value));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(PageLayout, null,
            React.createElement(Box, { height: "75vh" },
                React.createElement(Box, { mb: 1 },
                    React.createElement(Typography, { variant: "h4" }, "\u0421\u0438\u0441\u0442\u0435\u043C\u044B")),
                React.createElement(Box, { mb: 2 },
                    React.createElement(Button, { variant: "contained", color: "primary", startIcon: React.createElement(AddCircleIcon, null), onClick: () => navigate(routes.systemAdd) }, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C")),
                React.createElement(Paper, { className: classes.tableRoot },
                    React.createElement(TableContainer, { className: classes.tableContainer },
                        React.createElement(Table, { stickyHeader: true },
                            React.createElement(TableHead, null,
                                React.createElement(TableRow, null, columns.map((column) => (React.createElement(TableCell, { key: column.value, align: column.align, style: { minWidth: column.minWidth } }, column.label))))),
                            React.createElement(TableBody, null, systemsList && systemsList?.huHeadSystemInfos
                                ? systemsList?.huHeadSystemInfos.map((system) => (React.createElement(TableRow, { className: classes.tableBodyRow, key: system.guid, hover: true, onClick: () => navigate(`/system/${system.guid}`) }, columns.map((column) => (React.createElement(TableCell, { key: column.value, align: column.align }, column.render(system)))))))
                                : null))),
                    totalCount > 0 && (React.createElement(Box, { display: "flex", alignItems: "center", justifyContent: "flex-end", pl: 2 },
                        React.createElement(TextField, { className: classes.pageInput, size: "small", variant: "outlined", type: "number", label: `Страница из ${totalPages}`, inputProps: { min: 1, max: totalPages }, value: pageDraft, onChange: (event) => setPageDraft(event.target.value), onBlur: applyPageDraft, onKeyDown: handlePageKeyDown }),
                        React.createElement(TablePagination, { labelRowsPerPage: 'Cтрок на странице', rowsPerPageOptions: rowsPerPageSetting, component: "div", count: totalCount, rowsPerPage: pageSize, page: pageNumber - 1, onChangePage: handleChangePage, onChangeRowsPerPage: handleChangeRowsPerPage })))))),
        React.createElement(Drawer, { isOpen: isAddingSystem, onClose: handleCloseSystem }, isAddingSystem && React.createElement(SystemCreate, { onCreated: handleSystemCreated })),
        React.createElement(Drawer, { isOpen: Boolean(openedSystem), onClose: handleCloseSystem }, openedSystem && (React.createElement(SystemDetails, { key: openedSystem.guid, system: openedSystem, onClose: handleCloseSystem })))));
};
